import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAddBuyers } from "@/hooks/useBuyerTracker";

interface BuyerLite { id: string; name: string; subType?: string | null; headquarters?: string | null }

export function AddBuyersDialog({ clientId, open, onOpenChange }: {
  clientId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [markTeaser, setMarkTeaser] = useState(true);
  const add = useAddBuyers(clientId);

  // /api/buyers returns { data: BuyerLite[], pagination, stats }
  const { data: buyers = [] } = useQuery<BuyerLite[]>({
    queryKey: ["buyers-for-add"],
    queryFn: () =>
      apiRequest("GET", `/api/buyers?limit=all`)
        .then((r) => r.json())
        .then((d) => {
          // Handle all known shapes: array, { data }, { rows }, { buyers }
          if (Array.isArray(d)) return d;
          if (d.data) return d.data;
          return d.rows ?? d.buyers ?? [];
        }),
    enabled: open,
    staleTime: 60_000,
  });

  const filtered = buyers
    .filter((b) => b.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 100);

  const submit = () =>
    add.mutate(
      { buyerIds: [...picked], markTeaserNow: markTeaser },
      { onSuccess: () => { setPicked(new Set()); onOpenChange(false); } }
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add buyers from network</DialogTitle></DialogHeader>
        <Input placeholder="Search buyers…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="max-h-72 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-900">
          {filtered.map((b) => (
            <label key={b.id} className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm last:border-0 dark:border-slate-900">
              <input
                type="checkbox"
                checked={picked.has(b.id)}
                onChange={() =>
                  setPicked((s) => { const n = new Set(s); n.has(b.id) ? n.delete(b.id) : n.add(b.id); return n; })
                }
              />
              <span className="font-medium">{b.name}</span>
              <span className="text-xs text-slate-400">{[b.subType, b.headquarters].filter(Boolean).join(" · ")}</span>
            </label>
          ))}
          {filtered.length === 0 && <div className="p-4 text-center text-sm text-slate-400">No matches</div>}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <input type="checkbox" checked={markTeaser} onChange={(e) => setMarkTeaser(e.target.checked)} /> Mark teaser sent now
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={picked.size === 0 || add.isPending} onClick={submit}>
            Add {picked.size || ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
